import React, { useCallback } from 'react';
import { useLocalSearchParams, useRouter } from 'expo-router';

import type { AddressType } from '@sg/garnish';
import { useIsFocused } from '@sg/garnish';

import { ConfirmLocation } from '../../../../components/ConfirmLocation';
import { useLastValidOrder } from '../../../../context/Order';
import { useCart } from '../../../../hooks';
import { useNavigateBack } from '../../../../navigation';
import { useLocationConfirm } from '../../hooks';
import { useOrderProductConfirmLocationScreenData } from './hooks/useOrderProductConfirmLocationScreenData';

export const OrderProductConfirmLocationScreen = () => {
  const { productSlug: baseProductSlug } = useLocalSearchParams<{
    productSlug: string;
  }>();

  const router = useRouter();
  const isFocused = useIsFocused();
  const navigateBack = useNavigateBack();

  const { lastValidOrder: lastPlacedOrder } = useLastValidOrder();
  const { cart, deliveryDetails, cartOrderType, isCartEmpty } = useCart();

  const lastPlacedOrderDeliveryDetail = lastPlacedOrder?.deliveryOrderDetail;
  const hasCart = !isCartEmpty;
  const hasLocationHistory = hasCart || lastPlacedOrder;

  const { region, restaurant, deliveryAddress, pins, isFetching } =
    useOrderProductConfirmLocationScreenData({
      restaurantSlug: hasCart ? cart?.restaurant.id : lastPlacedOrder?.restaurant.id,
      addressId: hasCart
        ? deliveryDetails?.address?.id
        : lastPlacedOrder?.deliveryOrderDetail?.address?.id,
    });

  const deliveryVendorName = hasCart
    ? deliveryDetails.vendor
    : lastPlacedOrderDeliveryDetail?.vendor;
  const deliveryVendorRestaurantId = hasCart
    ? deliveryDetails.vendorRestaurantId
    : lastPlacedOrderDeliveryDetail?.vendorRestaurantId;
  const deliveryFee = hasCart
    ? deliveryDetails.deliveryFee
    : lastPlacedOrderDeliveryDetail?.deliveryFee;
  const orderType = (
    hasCart ? cartOrderType : lastPlacedOrder?.orderType
  )?.toLowerCase() as AddressType;

  // ─────────────────────────────────────────────────────────────────────

  // @ts-expect-error - isAddingLineItemToCart no longer returned by useLocationConfirm
  const { isFetchingProduct, isAddingLineItemToCart, handleLocationConfirm } = useLocationConfirm({
    baseProductSlug,
    deliveryFee,
    deliveryVendorName,
    deliveryVendorRestaurantId,
    restaurantId: restaurant?.id,
    restaurantSlug: restaurant?.slug,
    deliveryAddressId: deliveryAddress?.id,
  });

  // ─────────────────────────────────────────────────────────────────────

  const navigateToSearchLocation = useCallback(() => {
    router.navigate({
      pathname: '/order-product/[productSlug]/search-location',
      params: { productSlug: baseProductSlug },
    });
  }, [baseProductSlug, router]);

  const handleDismiss = useCallback(() => {
    navigateBack();
  }, [navigateBack]);

  // ─────────────────────────────────────────────────────────────────────

  if (!isFocused) return null;

  return (
    <ConfirmLocation
      deliveryAddress={deliveryAddress}
      isFetching={isFetching}
      orderType={orderType === 'delivery' ? 'delivery' : 'pickup'}
      // @ts-expect-error TS(2322): Type '{ readonly __typename?: "Restaurant" | undef... Remove this comment to see the full error message
      restaurant={restaurant}
      region={region}
      pins={pins}
      isLoading={isFetchingProduct || isAddingLineItemToCart}
      onContinue={handleLocationConfirm}
      onClose={handleDismiss}
      onMoreLocations={hasCart ? undefined : navigateToSearchLocation}
      hasError={!region || !restaurant || !hasLocationHistory}
    />
  );
};
